import { Injectable, NgZone, Inject, OnDestroy } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { LoggingService } from '@pxl/models/contracts';

export interface KeyCommand {
    /** e.g. 'Escape', 'KeyS', 'ArrowUp', … */
    code: string;
    /** the callback to invoke when that key is pressed */
    action: () => void;
}

@Injectable({ providedIn: 'root' })
export class KeyboardShortcutsService implements OnDestroy {
    private readonly commands = new Map<string, () => void>();
    private sub: Subscription;

    public constructor(
        private readonly ngZone: NgZone,
        private readonly loggingService: LoggingService,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {
        this.ngZone.runOutsideAngular(() => {
            this.sub = fromEvent<KeyboardEvent>(this.document, 'keydown')
                .pipe(filter((evt) => this.commands.has(evt.code)))
                .subscribe((evt) => {
                    this.ngZone.run(() => {
                        const action = this.commands.get(evt.code);
                        action();
                    });
                });
        });
    }

    public register(cmd: KeyCommand) {
        this.loggingService.debug('Registering keyboard shortcut', cmd.code);
        this.commands.set(cmd.code, cmd.action);
    }

    public unregister(code: string) {
        this.loggingService.debug('Unregistering keyboard shortcut', code);
        this.commands.delete(code);
    }

    public ngOnDestroy() {
        this.sub.unsubscribe();
    }
}
